// app/providers.tsx
"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, Suspense, useRef } from "react";
import { usePostHog } from "posthog-js/react";
import { authClient } from "@/lib/client/betterauth/auth-client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const environment = process.env.NODE_ENV === "production" ? "prod" : "dev";

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host:
        process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
      person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
      capture_pageview: false, // Disable automatic pageview capture, as we capture manually
    });

    posthog.register({
      environment,
      stack: "nextjs",
    });

    posthog.startExceptionAutocapture({
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: true,
    });
  }, []);

  return (
    <PHProvider client={posthog}>
      <PostHogUserIdentifier />
      <SuspendedPostHogPageView />
      {children}
    </PHProvider>
  );
}

// Component to identify the user in PostHog
function PostHogUserIdentifier() {
  const { data: session } = authClient.useSession();
  const posthog = usePostHog();

  useEffect(() => {
    // Identify the user when session data is available
    if (session?.user?.id && posthog) {
      // Identify the user with their ID
      posthog.identify(session.user.id, {
        email: session.user.email,
        name: session.user.name,
        organisationName: session.user.organisation?.name,
        organisationId: session.user.organisationId,
      });
    }
  }, [session, posthog]);

  return null;
}

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();

  // Track pageviews
  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname;
      if (searchParams.toString()) {
        url = url + "?" + searchParams.toString();
      }

      posthog.capture("$pageview", { $current_url: url });
    }
  }, [pathname, searchParams, posthog]);

  return null;
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}
